<script lang="ts">
  import { ButtonExpander } from '@athena-lib/ButtonExpander';
  import type { CustomValues, FileAppData, Orders } from '@athena-lib/ProductWizard/types';
  import type { PriceAndQuantityOptionInterface } from '@athena-lib/ProductWizard/model/PriceAndQuantityOptionInterface';
  import { ProductWizard, toJSON } from '@stickerapp-org/athena/ProductWizard';
  import { PageSection } from '@athena-lib/Page';
  import { storyblokEditable } from '@storyblok/svelte';
  import { Spinner } from '@athena-lib/Icons';
  import { Overlay } from '@athena-lib/Overlay';
  import type { ProductItem } from '@stickerapp-org/nomisma';
  import Editor from './Editor.svelte';
  import type { ProductWizardWithAtlas } from '$lib/server/expanders/productWizard';
  import type { EditorStoryblokWithAtlas } from '$lib/server/expanders/editorPage';
  import { trackProductWizard } from '$lib/gtm/event-trigger';
  import { GtmEventNames, type Tracking } from '$lib/gtm/types';
  import { ATLAS_RESPONSE_ELEMENT_ID } from '$lib/scope-atlas-css-constants';

  const { blok }: { blok: ProductWizardWithAtlas } = $props();

  const gtmParams = [
    'select_product',
    'select_material',
    'select_laminate',
    'select_outline',
    'select_size',
    'select_quantity',
  ] as const;

  const editorBlok: EditorStoryblokWithAtlas = {
    ...blok,
    atlas: { ...blok.atlas, data: undefined },
    useNewCheckout: blok.useNewCheckout,
  };

  let loadingCss = $state(false);

  const order: Orders = $derived.by(() => {
    if (!blok.columnOrder || (blok.columnOrder && blok.columnOrder.length === 0)) {
      return undefined;
    }
    return Array.from(new Set([...blok.columnOrder, 'size', 'quantity']));
  });

  const onOpenEditor = async (
    item: ProductItem,
    _price?: PriceAndQuantityOptionInterface,
    file?: FileAppData,
    customValues?: CustomValues,
  ) => {
    let atlasOverlay = document.getElementById('overlay');
    if (atlasOverlay) {
      atlasOverlay.classList.add('invisible');
    }
    await loadStyle(blok.atlas.stickerappCSS, 'stickerapp-css-' + ATLAS_RESPONSE_ELEMENT_ID);
    const queryString = new URLSearchParams({
      productItem: JSON.stringify(toJSON(item)),
    });

    if (file) {
      queryString.append('file', JSON.stringify(file));
    }

    const targetElement = document.getElementById('wizform');
    const url = '/editor/new_product?' + queryString.toString();
    const editorLink = document.createElement('a');
    editorLink.href = url;
    // Atlas is currently listening on data-toggle=editor
    editorLink.setAttribute('data-toggle', 'editor');
    editorLink.classList.add('hidden');
    targetElement?.appendChild(editorLink);
    editorLink.click();

    trackGTM(item, customValues);
  };

  const trackGTM = (item: ProductItem, customValues?: CustomValues) => {
    const getParams = () => {
      const obj: Record<string, string | undefined> = {};
      gtmParams.forEach((param) => {
        switch (param) {
          case 'select_product':
          case 'select_material':
          case 'select_laminate':
          case 'select_outline':
            obj[param] =
              param === 'select_product'
                ? item.getProductName()
                : param === 'select_outline'
                  ? attributeLabel('sheet_name')
                  : attributeLabel(param.split('_')[1]);
            break;
          case 'select_size':
            if (customValues?.customSize) {
              const widthMM = attributeLabel('width_mm');
              const heightMM = attributeLabel('height_mm');
              if (widthMM && heightMM) {
                const value = `${convertMMToCM(Number(widthMM))}x${convertMMToCM(Number(heightMM))} cm`;
                obj['custom_size'] = value;
              }
            } else {
              obj[param] = attributeLabel('size');
            }
            break;
          case 'select_quantity':
            if (customValues?.customQuantity) {
              obj['custom_quantity'] = attributeLabel('quantity');
            } else {
              obj[param] = attributeLabel('quantity');
            }
            break;
        }
      });
      return obj;
    };
    const trackWithLabel = (eventName: GtmEventNames, label: string, params: object) => {
      let event: Tracking = {
        event: eventName,
        event_params: { event_action: 'wizard', label: label, ...params },
      };
      trackProductWizard(event);
    };

    const attributeLabel = (attr: string) => item.getAttributes()[attr]?.toString();

    const convertMMToCM = (mm: number) => {
      return Number((mm / 10).toFixed(1));
    };

    const params = getParams();

    if (customValues?.uploadEvent) {
      trackWithLabel(GtmEventNames.OpenWizard, 'upload_file', params);
    } else {
      trackWithLabel(GtmEventNames.OpenWizard, 'next', params);
    }
  };

  function loadStyle(url: string, id: string) {
    loadingCss = true;
    return new Promise((resolve, reject) => {
      if (document.getElementById(id)) {
        loadingCss = false;
        resolve(true); // Script already exists, dont need to load again
        return;
      }

      const style = document.createElement('link');
      style.href = url;
      style.type = 'text/css';
      style.rel = 'stylesheet';
      style.media = 'all';
      style.id = id;
      style.onload = () => {
        loadingCss = false;
        resolve(true);
      };
      style.onerror = () => reject(new Error(`Failed to load script ${url}`));

      document.head.appendChild(style);
    });
  }
</script>

<PageSection
  id={blok.elementId}
  action={storyblokEditable}
  actionArg={blok}
  ySpace={{ top: blok.topPadding ?? 'medium', bottom: blok.bottomPadding ?? 'medium' }}
  backgroundColor={blok.backgroundColor}
>
  <ButtonExpander
    useExpander={blok.useExpandButton || false}
    text={blok.expandText || ''}
    alignment={blok.expandAlignment}
    size={blok.expandSize}
    full={blok.full}
  >
    <ProductWizard
      title={blok.title}
      description={blok.description}
      alignment="none"
      data={blok?.wizard}
      colOrder={order}
      columns={{ sm: 1, md: 1, lg: blok.verticalAlignment ? 1 : 12 }}
      verticalAlignment={blok.verticalAlignment}
      materialPresetOnly={blok.materialPresetOnly}
      availableMaterials={blok.availableMaterials}
      sizeType={blok.sizeType}
      background={'none'}
      onContinue={onOpenEditor}
      stickyProductList={blok.stickyProductList}
    ></ProductWizard>
    <Overlay
      isOpen={loadingCss}
      onClose={() => {}}
      label={'Loading'}
      overlayClass={'fixed bottom-0 left-0 right-0 top-0'}
      lockScrolling={false}
    >
      <div class="relative left-1/2 top-1/2 justify-center text-st-black">
        <Spinner sizeClass="w-20 h-20" />
      </div>
    </Overlay>
  </ButtonExpander>
</PageSection>
<Editor blok={editorBlok} skipCss></Editor>
